import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  Trophy, 
  Target, 
  Users, 
  Calendar, 
  Award,
  Star,
  TrendingUp,
  Medal,
  Flame,
  Zap,
  CheckCircle,
  Clock,
  Plus
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const EnhancedChallenges = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('active');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    target: '',
    reward_points: '',
    start_date: '',
    end_date: '',
    difficulty: 'medium'
  });

  const handleCreateChallenge = async () => {
    if (!formData.name || !formData.target || !formData.reward_points) {
      alert('Please fill in required fields: Name, Target, Reward Points');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/challenges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          target_count: parseInt(formData.target),
          reward_points: parseInt(formData.reward_points),
          start_date: formData.start_date,
          end_date: formData.end_date,
          difficulty: formData.difficulty
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Challenge created successfully!');
        setShowCreateModal(false);
        setFormData({
          name: '',
          description: '',
          target: '',
          reward_points: '',
          start_date: '',
          end_date: '',
          difficulty: 'medium'
        });
      }
    } catch (error) {
      console.error('Error creating challenge:', error);
      alert('Error creating challenge');
    }
  };

  const activeChallenges = [
    {
      id: 1,
      name: 'November Referral Sprint',
      description: 'Complete 50 referrals this month',
      target: 50,
      current: 34,
      participants: 28,
      reward: 500,
      status: 'active',
      endsIn: '12 days',
      icon: '🏃',
      type: 'referral',
      difficulty: 'medium'
    },
    {
      id: 2,
      name: 'Patient Satisfaction Star',
      description: 'Achieve 100% satisfaction rating',
      target: 100,
      current: 87,
      participants: 45,
      reward: 1000,
      status: 'active',
      endsIn: '18 days',
      icon: '⭐',
      type: 'quality',
      difficulty: 'hard'
    },
    {
      id: 3,
      name: 'Q4 Growth Challenge',
      description: 'Reach 200 new patient referrals',
      target: 200,
      current: 156,
      participants: 62,
      reward: 2000,
      status: 'active',
      endsIn: '45 days',
      icon: '📈',
      type: 'growth',
      difficulty: 'hard'
    },
    {
      id: 4,
      name: 'Weekly Warrior',
      description: 'Complete 10 referrals this week',
      target: 10,
      current: 7,
      participants: 85,
      reward: 200,
      status: 'active',
      endsIn: '3 days',
      icon: '⚡',
      type: 'referral',
      difficulty: 'easy'
    }
  ];

  const completedChallenges = [
    {
      id: 5,
      name: 'October Excellence',
      target: 40,
      current: 45,
      reward: 500,
      completedDate: '2025-10-31',
      icon: '🏆',
      rank: 3
    },
    {
      id: 6,
      name: 'Speed Demon',
      target: 20,
      current: 23,
      reward: 300,
      completedDate: '2025-10-25',
      icon: '⚡',
      rank: 1
    }
  ];

  const leaderboard = [
    { rank: 1, name: 'Dr. Sarah Johnson', points: 4850, avatar: 'SJ', trend: 'up', badge: '🥇' },
    { rank: 2, name: 'Dr. Michael Chen', points: 4520, avatar: 'MC', trend: 'up', badge: '🥈' },
    { rank: 3, name: 'Dr. Gerard Nikodem', points: 4200, avatar: 'GN', trend: 'same', badge: '🥉' },
    { rank: 4, name: 'Dr. David Schertzer', points: 3890, avatar: 'DS', trend: 'down', badge: '' },
    { rank: 5, name: 'Dr. Emily Rodriguez', points: 3650, avatar: 'ER', trend: 'up', badge: '' }
  ];

  const achievements = [
    { id: 1, name: 'First Step', description: 'Complete your first challenge', icon: '🎯', unlocked: true },
    { id: 2, name: 'Streak Master', description: 'Complete challenges 7 days in a row', icon: '🔥', unlocked: true },
    { id: 3, name: 'Top 10', description: 'Reach top 10 on leaderboard', icon: '🏆', unlocked: true },
    { id: 4, name: 'Century Club', description: 'Complete 100 referrals', icon: '💯', unlocked: false },
    { id: 5, name: 'Perfect Score', description: 'Achieve 100% satisfaction', icon: '⭐', unlocked: false },
    { id: 6, name: 'Legend', description: 'Rank #1 on leaderboard', icon: '👑', unlocked: false }
  ];

  const getDifficultyBadge = (difficulty) => {
    const colors = {
      easy: 'bg-green-100 text-green-800 border-green-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      hard: 'bg-red-100 text-red-800 border-red-200'
    };
    return <Badge className={colors[difficulty]}>{difficulty}</Badge>;
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
            <Trophy className="h-8 w-8 mr-3 text-[#D4AF37]" />
            Challenges & Achievements
          </h1>
          <p className="text-gray-600">Compete, earn rewards, and climb the leaderboard</p>
        </div>
        {user?.role === 'superadmin' || user?.role === 'admin' ? (
          <Button 
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Challenge
          </Button>
        ) : null}
      </div>

      {/* Stats Overview */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Challenges</p>
                <p className="text-2xl font-bold text-[#0A1628]">{activeChallenges.length}</p>
              </div>
              <Trophy className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Your Rank</p>
                <p className="text-2xl font-bold text-[#0A1628]">#12</p>
              </div>
              <Medal className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-[#0A1628]">{completedChallenges.length}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Points</p>
                <p className="text-2xl font-bold text-[#0A1628]">3,450</p>
              </div>
              <Star className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Challenge Tabs */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex space-x-2 mb-6">
                <Button
                  variant={activeTab === 'active' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('active')}
                  className={activeTab === 'active' ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
                >
                  <Flame className="h-4 w-4 mr-2" />
                  Active
                </Button>
                <Button
                  variant={activeTab === 'completed' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('completed')}
                  className={activeTab === 'completed' ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Completed
                </Button>
              </div>

              {activeTab === 'active' ? (
                <div className="space-y-4">
                  {activeChallenges.map((challenge) => (
                    <Card key={challenge.id} className="hover:shadow-lg transition-shadow border-l-4 border-[#D4AF37]">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className="text-4xl">{challenge.icon}</div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h3 className="font-semibold text-lg text-[#0A1628]">{challenge.name}</h3>
                                {getDifficultyBadge(challenge.difficulty)}
                              </div>
                              <p className="text-sm text-gray-600 mb-3">{challenge.description}</p>
                              
                              {/* Progress Bar */}
                              <div className="mb-3">
                                <div className="flex justify-between text-sm mb-2">
                                  <span className="text-gray-600">Progress</span>
                                  <span className="font-semibold text-[#0A1628]">
                                    {challenge.current} / {challenge.target}
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3">
                                  <div 
                                    className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] h-3 rounded-full transition-all duration-500" 
                                    style={{ width: `${Math.min((challenge.current / challenge.target) * 100, 100)}%` }}
                                  ></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  {Math.round((challenge.current / challenge.target) * 100)}% complete
                                </p>
                              </div>

                              <div className="flex items-center space-x-4 text-sm text-gray-600">
                                <span className="flex items-center">
                                  <Users className="h-4 w-4 mr-1" />
                                  {challenge.participants} participating
                                </span>
                                <span className="flex items-center">
                                  <Clock className="h-4 w-4 mr-1" />
                                  {challenge.endsIn}
                                </span>
                                <span className="flex items-center">
                                  <Star className="h-4 w-4 mr-1 text-[#D4AF37]" />
                                  {challenge.reward} points
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <Button 
                          className="w-full bg-[#0A1628] text-[#D4AF37] hover:bg-[#1a2332]"
                          onClick={() => navigate(`/challenges/${challenge.id}`)}
                        >
                          View Details
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {completedChallenges.map((challenge) => (
                    <Card key={challenge.id} className="bg-green-50 border-green-200">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="text-4xl">{challenge.icon}</div>
                            <div>
                              <h3 className="font-semibold text-lg text-[#0A1628]">{challenge.name}</h3>
                              <p className="text-sm text-gray-600">Completed {challenge.completedDate}</p>
                              <div className="flex items-center space-x-2 mt-2">
                                <Badge className="bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Completed
                                </Badge>
                                <span className="text-sm text-gray-600">Rank: #{challenge.rank}</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-[#D4AF37]">+{challenge.reward}</p>
                            <p className="text-xs text-gray-600">points earned</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Achievements */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Award className="h-6 w-6 mr-2 text-[#D4AF37]" />
                Achievements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {achievements.map((achievement) => (
                  <div 
                    key={achievement.id}
                    className={`p-4 rounded-lg border-2 ${
                      achievement.unlocked 
                        ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-[#D4AF37]' 
                        : 'bg-gray-50 border-gray-200 opacity-60'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-3xl">{achievement.icon}</div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-[#0A1628]">{achievement.name}</h4>
                        <p className="text-xs text-gray-600">{achievement.description}</p>
                        {achievement.unlocked && (
                          <Badge className="mt-2 bg-[#D4AF37] text-[#0A1628]">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Unlocked
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Leaderboard Sidebar */}
        <div className="space-y-6">
          <Card className="bg-gradient-to-br from-[#0A1628] to-[#1a2332] text-white">
            <CardHeader>
              <CardTitle className="flex items-center text-[#D4AF37]">
                <Trophy className="h-6 w-6 mr-2" />
                Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {leaderboard.map((entry) => (
                  <div 
                    key={entry.rank}
                    className={`p-3 rounded-lg ${
                      entry.rank <= 3 
                        ? 'bg-white/10 border-2 border-[#D4AF37]' 
                        : 'bg-white/5'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{entry.badge || `#${entry.rank}`}</div>
                      <Avatar className="h-10 w-10 border-2 border-[#D4AF37]">
                        <AvatarFallback className="bg-[#D4AF37] text-[#0A1628] font-bold">
                          {entry.avatar}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{entry.name}</p>
                        <p className="text-xs text-[#D4AF37]">{entry.points.toLocaleString()} points</p>
                      </div>
                      {entry.trend === 'up' && <TrendingUp className="h-4 w-4 text-green-400" />}
                    </div>
                  </div>
                ))}
              </div>
              <Button className="w-full mt-4 bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]">
                View Full Leaderboard
              </Button>
            </CardContent>
          </Card>

          {/* Quick Tips */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Zap className="h-5 w-5 mr-2 text-blue-600" />
                Quick Tips
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-gray-700">
              <p>💡 Complete challenges to earn bonus points</p>
              <p>🔥 Maintain streaks for multipliers</p>
              <p>🏆 Top 3 finishers get bonus rewards</p>
              <p>⭐ Unlock achievements for exclusive badges</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Challenge Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Create New Challenge</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Challenge Name *</label>
                <Input
                  placeholder="November Referral Sprint"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea
                  placeholder="Complete 50 referrals this month..."
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Target Count *</label>
                  <Input
                    type="number"
                    placeholder="50"
                    value={formData.target}
                    onChange={(e) => setFormData({...formData, target: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Reward Points *</label>
                  <Input
                    type="number"
                    placeholder="500"
                    value={formData.reward_points}
                    onChange={(e) => setFormData({...formData, reward_points: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Difficulty</label>
                  <select
                    className="w-full px-3 py-2 border rounded-md"
                    value={formData.difficulty}
                    onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Start Date</label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Date</label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="flex space-x-2 pt-4">
                <Button 
                  className="flex-1 bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
                  onClick={handleCreateChallenge}
                >
                  Create Challenge
                </Button>
                <Button 
                  onClick={() => setShowCreateModal(false)} 
                  variant="outline" 
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default EnhancedChallenges;
